/** Theme selection.
 *
 * Three states, not two: "system" stamps nothing on <html> and lets
 * prefers-color-scheme decide, while "light"/"dark" stamp data-theme and win
 * over the media query in both directions. A two-state toggle cannot express
 * "follow the OS", which is what most people actually want.
 *
 * The choice is a rendering preference, so localStorage is the right home for
 * it. It is read once at startup, before React renders, so there is no flash
 * of the wrong theme for a user whose choice differs from their OS setting.
 * The Content-Security-Policy on this origin forbids inline script, so this
 * runs from the module entry rather than a <script> in index.html.
 */

export type ThemeChoice = "system" | "light" | "dark";

const KEY = "oxminter.theme";

export function isThemeChoice(v: unknown): v is ThemeChoice {
  return v === "system" || v === "light" || v === "dark";
}

/** The stored choice, or "system" when nothing valid is stored. */
export function readTheme(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return isThemeChoice(v) ? v : "system";
  } catch {
    // Private browsing / storage disabled. Fall back rather than throw:
    // a wallet console must render even when storage is unavailable.
    return "system";
  }
}

/** Stamp the choice on <html>. "system" removes the attribute entirely. */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", choice);
}

export function writeTheme(choice: ThemeChoice): void {
  try {
    if (choice === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    /* Storage unavailable: the choice still applies for this session. */
  }
}

/** What the page is actually showing right now, resolving "system". */
export function effectiveTheme(choice: ThemeChoice): "light" | "dark" {
  if (choice !== "system") return choice;
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  } catch {
    return "dark";
  }
}
